import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import useOffersUI from 'api/useOffersUI';
import usePermissions from 'api/usePermissions';
import useTheme from 'api/useTheme';
import { ETaskId, OfferUI } from 'common/contracts';
import { useFormik } from 'formik';
import { useUnsavedChanges } from 'hooks/useUnsavedChanges';
import { useChecklistState } from 'state/hooks/setup-checklist.hook';
import { EToasterStatus, showToast } from 'utils/showToast';
import { SUBTITLE_SUPPORTED_TEMPLATES } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';

import Button from 'components/Button/Button';

import { OfferDesignPreview } from './components/OfferDesignPreview/OfferDesignPreview';
import { INITIAL_VALUES_BUILDERS, OFFER_TYPE_CONFIG } from './OfferDesignForm.consts';
import { buildInitialValues } from './utils';

export const OfferDesignForm = () => {
  const { publisherId, offerUIId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const isDup = location.pathname.includes('/dup/');
  const isEdit = !!offerUIId && !isDup;
  const searchParams = new URLSearchParams(location.search);
  const offerType = (searchParams.get('type') as OfferDesignTypes) || OfferDesignTypes.Bundle;
  const offerSubType = searchParams.get('subType') || '';
  const config = OFFER_TYPE_CONFIG[offerType];

  const { canUserEdit } = usePermissions();
  const { setTaskStatus, hasCategories, isWebStore } = useChecklistState();
  const { addOfferUI, updateOfferUI, getOfferUI, getOffersUI } = useOffersUI(publisherId, offerUIId);
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);

  const [isNameUnique, setIsNameUnique] = useState(true);
  const [isExternalIdUnique, setIsExternalIdUnique] = useState(true);

  const shouldDisplaySubTitle = useMemo(
    () => !!generalTheme?.data?.specialOffersInternalViewModel &&
      SUBTITLE_SUPPORTED_TEMPLATES.includes(generalTheme.data.specialOffersInternalViewModel),
    [generalTheme.data?.specialOffersInternalViewModel]
  );

  const initialValues = useMemo(
    () => buildInitialValues(offerType, getOfferUI?.data, isDup, offerSubType, INITIAL_VALUES_BUILDERS),
    [getOfferUI?.data, isDup, offerType, offerSubType]
  );

  const formTitle = useMemo(() => {
    if (isEdit) return 'Edit offer design';
    if (isDup) return 'Duplicate offer design';
    return 'Create new offer design';
  }, [isEdit, isDup]);

  const handleClose = useCallback(() => navigate('../'), [navigate]);

  const handleSubmitSuccess = useCallback(() => {
    showToast({
      message: `Offer Design ${isEdit ? 'saved' : 'added'} successfully`,
      status: EToasterStatus.SUCCESS
    });
    if (!isEdit && hasCategories && isWebStore) {
      setTaskStatus(ETaskId.CREATE_OFFER_DESIGN);
    }
    (isEdit ? getOfferUI : getOffersUI).refetch();
    navigate('../');
  }, [isEdit, hasCategories, isWebStore, setTaskStatus, getOfferUI, getOffersUI, navigate]);

  const handleSubmitError = useCallback(() => {
    showToast({
      message: `Error ${isEdit ? 'editing' : 'adding'} Offer Design`,
      status: EToasterStatus.ERROR
    });
  }, [isEdit]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    submitForm,
    isValid,
    dirty,
    setFieldValue
  } = useFormik({
    validateOnMount: true,
    enableReinitialize: true,
    initialValues,
    validationSchema: config?.schema,
    onSubmit: async (formValues) => {
      if (!config) return;
      const payload = config.buildPayload(formValues, offerType);
      const callbacks = { onSuccess: handleSubmitSuccess, onError: handleSubmitError };

      if (isEdit && offerUIId) {
        updateOfferUI.mutate({ offerId: offerUIId, form: payload }, callbacks);
      } else {
        addOfferUI.mutate(payload, callbacks);
      }
    }
  });

  const handleSave = useCallback(() => submitForm(), [submitForm]);

  const isFormValid = useMemo(
    () => isValid && (dirty || !isEdit) && canUserEdit && isNameUnique && isExternalIdUnique,
    [isValid, dirty, isEdit, canUserEdit, isNameUnique, isExternalIdUnique]
  );

  const formikProps = {
    values,
    errors: errors as Record<string, string | undefined>,
    touched: touched as Record<string, boolean | undefined>,
    handleChange,
    handleBlur,
    setFieldValue
  };

  useEffect(() => {
    if (offerUIId && publisherId) {
      getOfferUI.refetch();
    }
  }, [offerUIId, publisherId]);

  useEffect(() => {
    if (!isEdit && getOffersUI.data?.result) {
      const { result } = getOffersUI.data;
      setIsNameUnique(!result.some((o: OfferUI) => o.name === values.name));
      setIsExternalIdUnique(
        !result.some((o: OfferUI) => o.externalId === values.externalId)
      );
    }
  }, [getOffersUI.data, isEdit, values.name, values.externalId]);

  useUnsavedChanges({ dirty });

  if (!config) return null;

  const { FormFields } = config;

  return (
    <OfferFormLayout
      title={formTitle}
      isFormValid={isFormValid}
      onSave={handleSave}
      onClose={handleClose}
      previewContent={<OfferDesignPreview offerType={offerType} values={values} />}
      showPreviewToggle={false}
    >
      <div className="w-[600px] pt-10 pb-10 h-max">
        <Button
          variant="link"
          onClick={handleClose}
          className="p-0 h-auto font-semibold mb-5 gap-[9px] text-indigo-600 no-underline hover:no-underline hover:text-indigo-600"
        >
          <FontAwesomeIcon icon="fa-regular fa-arrow-left" />
          Back
        </Button>
        <FormFields
          {...formikProps}
          isNameUnique={isNameUnique}
          isExternalIdUnique={isExternalIdUnique}
          shouldDisplaySubTitle={shouldDisplaySubTitle}
          specialOffersInternalViewModel={generalTheme?.data?.specialOffersInternalViewModel}
        />
      </div>
    </OfferFormLayout>
  );
};
